import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { auth as authApi, roles as rolesApi } from '../services/api';
import NativeService from '../services/nativeService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permisos, setPermisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permisosLoading, setPermisosLoading] = useState(false);

    // Load user permissions from API
    const loadPermisos = useCallback(async () => {
        try {
            setPermisosLoading(true);
            const response = await rolesApi.getMyPermisos();
            const permisosArray = response?.permisos || [];
            setPermisos(permisosArray);
            // Cache in localStorage for faster subsequent loads
            localStorage.setItem('permisos', JSON.stringify(permisosArray));
            return permisosArray;
        } catch (error) {
            console.error('Error loading permissions:', error);
            setPermisos([]);
            return [];
        } finally {
            setPermisosLoading(false);
        }
    }, []);

    useEffect(() => {
        const initAuth = async () => {
            const storedUser = localStorage.getItem('user');
            const token = localStorage.getItem('token');
            const storedPermisos = localStorage.getItem('permisos');

            if (storedUser && token) {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);

                // Load cached permissions first for faster UI
                if (storedPermisos) {
                    try {
                        setPermisos(JSON.parse(storedPermisos));
                    } catch (e) {
                        console.error('Error parsing cached permisos:', e);
                    }
                }

                // Refresh user data and permissions from server
                await refreshUser();
                await loadPermisos();

                // Initialize push notifications on mobile
                NativeService.initPushNotifications();
            }
            setLoading(false);
        };

        initAuth();
    }, [loadPermisos]);

    const login = async (email, password) => {
        const response = await authApi.login(email, password);
        if (response.token) {
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(response.user));
            setUser(response.user);
            // Load permissions after login
            await loadPermisos();
            // Initialize push notifications on mobile
            NativeService.initPushNotifications();
        }
        return response;
    };

    // For Google OAuth callback - receives token and user directly
    const loginWithToken = async (token, userData) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        // Load permissions after login
        await loadPermisos();
        // Initialize push notifications on mobile
        NativeService.initPushNotifications();
    };

    // Refresh user data from server (e.g., after linking employee)
    const refreshUser = async () => {
        try {
            const response = await authApi.me();
            if (response.user) {
                localStorage.setItem('user', JSON.stringify(response.user));
                setUser(response.user);
                return response.user;
            }
        } catch (error) {
            console.error('Error refreshing user:', error);
            // If refresh fails, logout
            if (error.message.includes('401')) {
                logout();
            }
        }
        return null;
    };

    // Refresh permissions (after role change, etc.)
    const refreshPermissions = async () => {
        return await loadPermisos();
    };

    const logout = async () => {
        // RGPD: Unregister push notifications before clearing session
        try {
            // Get saved token before clearing localStorage
            const savedToken = localStorage.getItem('fcm_token');

            // Unregister from native push notifications
            await NativeService.unregisterPushNotifications();

            // Notify backend to deactivate the token
            if (savedToken) {
                const authToken = localStorage.getItem('token');
                await fetch('https://calendario.xerp.es/api/notifications/unregister-token', {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': authToken ? `Bearer ${authToken}` : ''
                    },
                    body: JSON.stringify({ token: savedToken })
                }).catch(e => console.error('Error unregistering token from backend:', e));
            }
        } catch (error) {
            console.error('Error during logout cleanup:', error);
        }

        // Clear local session
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permisos');
        localStorage.removeItem('fcm_token');
        setUser(null);
        setPermisos([]);
        window.location.href = '/login';
    };


    const isAuthenticated = () => !!user;
    const isAdmin = () => user?.role === 'admin';
    const needsEmployeeLink = () => user?.needsLink === true;

    /**
     * Check if user has a specific permission
     * @param {string} permissionCode - Permission code like 'calendario.ver' or 'usuarios.editar'
     * @returns {boolean} - True if user has the permission
     */
    const hasPermission = useCallback((permissionCode) => {
        // Admins have all permissions
        if (user?.role === 'admin') return true;
        // Check if permission exists in user's permissions array
        return permisos.includes(permissionCode);
    }, [user, permisos]);

    /**
     * Check if user has any of the specified permissions
     * @param {string[]} permissionCodes - Array of permission codes
     * @returns {boolean} - True if user has at least one of the permissions
     */
    const hasAnyPermission = useCallback((permissionCodes) => {
        if (user?.role === 'admin') return true;
        return permissionCodes.some(code => permisos.includes(code));
    }, [user, permisos]);

    /**
     * Check if user has all of the specified permissions
     * @param {string[]} permissionCodes - Array of permission codes
     * @returns {boolean} - True if user has all of the permissions
     */
    const hasAllPermissions = useCallback((permissionCodes) => {
        if (user?.role === 'admin') return true;
        return permissionCodes.every(code => permisos.includes(code));
    }, [user, permisos]);

    const authValue = useMemo(() => ({
        user,
        permisos,
        permisosLoading,
        login,
        loginWithToken,
        logout,
        refreshUser,
        refreshPermissions,
        isAuthenticated,
        isAdmin,
        needsEmployeeLink,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        loading
    }), [
        user,
        permisos,
        permisosLoading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        loading
    ]);

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
